/**
 * Kink Card Component
 * 
 * Displays a kink preview in a card format for browse/search results.
 * Shows name, category, weirdness score, and processing status.
 * Clickable to navigate to full kink detail page.
 * 
 * Fantasy Theme v2 - Gold accents, medieval aesthetic
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { to5Scale, render5Scale } from '../utils/displayConverters';
import { getAppealProfileHighlights } from '../utils/appealLenses';
import PopularityBadge from './PopularityBadge';

/**
 * Get weirdness badge styling based on score (1-5 scale)
 * Fantasy palette: Gold → Burgundy → Deep Wine
 */
function getWeirdnessClasses(score) {
  // score is 1-5
  if (score <= 1) return 'bg-[#C4A35A]/10 text-[#C4A35A] border-[#C4A35A]/20'; // Gold - Vanilla
  if (score <= 2) return 'bg-[#C4A35A]/20 text-[#C4A35A] border-[#C4A35A]/30'; // Warm Gold
  if (score <= 3) return 'bg-wine-500/15 text-wine-400 border-wine-500/25'; // Transition
  if (score <= 4) return 'bg-wine-500/25 text-wine-300 border-wine-500/40'; // Wine
  return 'bg-wine-600/40 text-wine-200 border-wine-400/60 font-black'; // Deep Wine
}

function KinkCard({ kink, onClick }) {
  const CardWrapper = onClick ? 'div' : Link;
  const wrapperProps = onClick
    ? { onClick: () => onClick(kink) }
    : { to: `/kink/${kink.id}` };

  const physicalPercent = kink.physicalAppeal || 50;
  const normalizedWeirdness = Math.ceil((kink.weirdnessScore || 50) / 20);
  const { primary: primaryAppealLens, supporting: supportingAppealLenses } = getAppealProfileHighlights(kink);
  const appealTags = [primaryAppealLens, ...supportingAppealLenses].filter(Boolean);

  return (
    <CardWrapper
      {...wrapperProps}
      className="group glass-card card-lift overflow-hidden cursor-pointer block border border-wine-800/30 hover:border-[#C4A35A]/30 transition-all duration-300"
    >
      {/* Visual Header - Reserved for future aspect-ratio images */}
      <div className="aspect-video bg-wine-950/50 flex items-center justify-center border-b border-wine-700/20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] to-transparent opacity-60" />
        {/* Subtle gold corner accent on hover */}
        <div className="absolute top-0 left-0 w-8 h-8 border-l-2 border-t-2 border-[#C4A35A]/0 group-hover:border-[#C4A35A]/30 transition-all duration-300" />
        <div className="absolute bottom-0 right-0 w-8 h-8 border-r-2 border-b-2 border-[#C4A35A]/0 group-hover:border-[#C4A35A]/30 transition-all duration-300" />
      </div>

      <div className="p-5">
        {/* Card Header */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <h3 className="font-fantasy text-lg font-semibold text-cream/90 group-hover:text-[#C4A35A] transition-colors leading-tight tracking-wide">
            {kink.name}
          </h3>

          {/* Level Badge - 1-5 Dots for consistency */}
          {kink.weirdnessScore && (
            <div className={`
              flex-shrink-0 inline-flex items-center justify-center 
              px-2 py-1 rounded-md border
              ${getWeirdnessClasses(normalizedWeirdness)}
            `} title={`Kinkiness: ${normalizedWeirdness}/5`}>
              {render5Scale(normalizedWeirdness)}
            </div>
          )}
        </div>

        {/* Category */}
        {kink.category && (
          <div className="mb-3">
            <span className="inline-block px-2 py-0.5 rounded bg-wine-900/30 border border-wine-700/30 text-[10px] uppercase tracking-wider font-bold text-wine-400">
              {kink.category}
            </span>
          </div>
        )}

        {/* Description - proper line clamp */}
        {(kink.summary || kink.description) && (
          <p className="text-sm text-cream/50 line-clamp-3 mb-4 leading-relaxed h-[3.75rem]">
            {kink.summary || kink.description}
          </p>
        )}

        {/* Phys:Psy Ratio Bar */}
        <div className="mb-4 pt-2 border-t border-wine-700/20">
          <div className="flex justify-between items-center mb-1.5">
            <span className="text-[10px] uppercase tracking-tighter text-cream/50 font-bold">Physical</span>
            <span className="text-[10px] uppercase tracking-tighter text-cream/50 font-bold">Mental</span>
          </div>
          <div className="ratio-bar">
            <div
              className="ratio-bar-fill"
              style={{ width: `${physicalPercent}%` }}
            />
          </div>
        </div>

        {appealTags.length > 0 && (
          <div className="mb-4 rounded-xl border border-wine-800/30 bg-dark-900/30 px-3 py-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-cream/35 font-bold">Appeals</div>

            <div className="mt-2 flex flex-wrap gap-1.5">
              {appealTags.map((facet) => (
                <span
                  key={facet.key}
                  className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[10px] font-bold uppercase tracking-wider ${facet.chipClass}`}
                >
                  <span>{facet.shortLabel}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t border-wine-700/20">
          {/* Popularity */}
          <div className="flex items-center gap-3">
            <PopularityBadge
              popularity={kink.popularity}
              velocity={kink.popularityVelocity}
              compact
            />
          </div>

          <span className="text-cream/50 group-hover:text-[#C4A35A] group-hover:translate-x-1 transition-all text-sm font-fantasy tracking-wide">
            Explore →
          </span>
        </div>
      </div>
    </CardWrapper>
  );
}

export default KinkCard;

